import { useState } from 'react';

import { Box, Button, Heading, Icon, Image, List, Paragraph, Tag, Text } from '@zillow/constellation-10';

import ApplicationSampleModal from '../Modal/ApplicationSampleModal';

import { StyledAsteriskAfter } from '../../StyledComponents/StyledComponents';

import propertyUtils from '../../utils/propertyUtils';

import ApplicationsCSEvents from '../../model/ClickstreamEvent/ApplicationsCSEvents';

import ApplicationDemo from '../../images/applicationDemo.png';

import { useSelector } from '../../selectors/useSelector';
import { IconSparkleFilled } from '@zillow/constellation-icons';

type Props = {
  children: React.ReactNode;
  isIntroModal?: boolean;
};

const ApplicationsWrapper = ({ children, isIntroModal = false }: Props) => {
  const {
    properties: { currentProperty },
    user,
  } = useSelector((state) => state);

  const isRoomForRent = propertyUtils.isRoomForRent({ currentProperty });

  const [displaySampleModal, setDisplaySampleModal] = useState(false);

  let intro = "You'll automatically receive these benefits:";
  if (isIntroModal) {
    intro = 'Enable Zillow applications so renters can apply directly from your listing. ' + intro;
  }

  return (
    <div data-testid="ApplicationsWrapper">
      <Box css={{ marginBottom: '400' }}>
        <Tag tone="info" css={{ marginRight: '200' }}>
          Free
        </Tag>
      </Box>
      <Heading level={4}>
        {isIntroModal ? (
          'Receive Zillow applications'
        ) : (
          <StyledAsteriskAfter>Receive Zillow applications for this listing?</StyledAsteriskAfter>
        )}
      </Heading>
      <Box css={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: '600' }}>
        <Box css={{ gridColumnStart: '1', gridColumnEnd: { base: 'span 12', xl: 'span 8' } }}>
          <div data-test="ApplicationsContent">
            <Paragraph css={{ marginTop: '600' }}>{intro}</Paragraph>
            <List.Root>
              <List.Item css={{ paddingBlock: '0' }}>Credit report from identity verified applicants</List.Item>
              <List.Item css={{ paddingBlock: '0' }}>
                Background check (includes eviction &amp; criminal record)
              </List.Item>
              <List.Item css={{ paddingBlock: '0' }}>Income &amp; employment history</List.Item>
              <List.Item css={{ paddingBlock: '0' }}>Residence history &amp; previous landlord contacts</List.Item>
            </List.Root>
            {!isRoomForRent && (
              <Box css={{ marginTop: '600', display: 'flex', flexDirection: 'row', alignItems: 'center' }}>
                <Icon
                  render={<IconSparkleFilled />}
                  size="md"
                  css={{ color: 'icon.accent.brand.hero', marginRight: '200' }}
                />
                <Text textStyle="body-sm">
                  Your listing gets an additional promotion when you choose to receive Zillow applications
                </Text>
              </Box>
            )}
          </div>
        </Box>

        <Box
          css={{
            display: 'grid',
            gridColumnStart: { base: '1', xl: '9' },
            gridColumnEnd: { base: 'span 12', xl: 'span 4' },
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Box css={{ display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
            <Image alt="sample application icon" src={ApplicationDemo} />

            <Button
              tone="brand"
              emphasis="filled"
              onClick={() => {
                setDisplaySampleModal(true);
                ApplicationsCSEvents.ClickSampleApplicationLinkInModal({ currentProperty, user });
              }}
              data-eventid={ApplicationsCSEvents.ClickSampleApplicationLinkInModal.data_event_id}
            >
              View sample
            </Button>
          </Box>
        </Box>

        <Box css={{ gridColumnStart: '1', gridColumnEnd: 'span 12' }}>{children}</Box>
      </Box>

      {displaySampleModal && <ApplicationSampleModal onClose={() => setDisplaySampleModal(false)} />}
    </div>
  );
};

export default ApplicationsWrapper;
